import {ChangeEvent, ReactElement, useState} from 'react'

import {FormControl, TextField, Button, MenuItem, InputLabel, Select} from '@material-ui/core'
import {Trans} from 'react-i18next'
import {PixAccountTypes, clientTypes} from '@bdswiss/common-enums'

import {ClientType, Maybe} from '../../../../../../../graphql/types'
import messages from '../../../../../../../assets/messages'
import {PIX_PAYOUT_FIELD_NAMES, PIX_PAYOUT_DEFAULT_FIELD_VALUES} from '../../../constants'
import {useStyles} from './styles'

type PixPayoutFieldsProps = {
  paymentFields: Record<string, any> | null
  onPaymentMethodSet: (fieldValues) => void
  clientType: Maybe<ClientType>
  customerIdentity: Maybe<string>
  onClose: () => void
}

const pixAccountTypesOptions = Object.values(PixAccountTypes)

const corporateClientTypes = Object.freeze([
  clientTypes.ibCorporate.value,
  clientTypes.affiliateCorporate.value,
]) as ClientType[]

export function PixPayoutFields(props: PixPayoutFieldsProps): ReactElement {
  const classes = useStyles()
  const {paymentFields, onPaymentMethodSet, onClose, clientType, customerIdentity} = props
  const [formFieldsValues, setFormFieldsValues] = useState(
    paymentFields || {
      ...PIX_PAYOUT_DEFAULT_FIELD_VALUES,
      [PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity]: customerIdentity || '',
    },
  )

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = event.target
    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleSelectFieldChange = (
    event: ChangeEvent<{name?: string | undefined; value: unknown}>,
  ) => {
    const {value, name} = event.target
    if (!name || !value) return

    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleSave = () => {
    onPaymentMethodSet(formFieldsValues)
    onClose()
  }

  // Corporate PIX activation
  let corporateClientDetected = clientType && corporateClientTypes.includes(clientType)
  corporateClientDetected = false

  const pixAccountTypes = pixAccountTypesOptions.filter((opt) => {
    if (corporateClientDetected) return opt.value !== PixAccountTypes.cpf.value
    return opt.value !== PixAccountTypes.cnpj.value
  })

  const pixAccountIdLabelsMap = {
    [PixAccountTypes.cpf.value]: <Trans {...messages.personalIdentificationNumber} />,
    [PixAccountTypes.email.value]: <Trans {...messages.email} />,
    [PixAccountTypes.phone.value]: <Trans {...messages.phoneNumber} />,
    [PixAccountTypes.evp.value]: <Trans {...messages.evp} />,
  }

  const fieldsVisibilityMap = buildFieldsVisibilityMap(formFieldsValues)
  const {errorsMap, hasErrors} = buildFieldsErrorStateMap(formFieldsValues, corporateClientDetected)

  const isEmptyFieldPresentInTheForm = Object.keys(formFieldsValues).some((key) => {
    const isVisible = fieldsVisibilityMap[key]
    const value = formFieldsValues[key]
    return isVisible && !value.toString().trim()
  })

  const customerIdentityLabel = corporateClientDetected
    ? messages.cnpjNumber
    : messages.personalIdentificationNumber

  const isSubmitDisabled = isEmptyFieldPresentInTheForm || hasErrors

  return (
    <>
      <FormControl fullWidth>
        <InputLabel>
          <Trans {...messages.pixAccountType} />
        </InputLabel>
        <Select
          name={PIX_PAYOUT_FIELD_NAMES.pixAccountType}
          value={formFieldsValues.pixAccountType}
          onChange={handleSelectFieldChange}
        >
          {pixAccountTypes.map(({value, label}) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <TextField
          required
          type="text"
          name={PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity}
          value={formFieldsValues.pixCustomerIdentity}
          label={<Trans {...customerIdentityLabel} />}
          onChange={handleTextFieldChange}
          error={errorsMap[PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity] !== null}
          helperText={errorsMap[PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity]}
        />
      </FormControl>
      {fieldsVisibilityMap['pixAccountId'] ? (
        <FormControl fullWidth>
          <TextField
            required
            type="text"
            name={PIX_PAYOUT_FIELD_NAMES.pixAccountId}
            value={formFieldsValues.pixAccountId}
            label={pixAccountIdLabelsMap[formFieldsValues.pixAccountType]}
            onChange={handleTextFieldChange}
            error={errorsMap[PIX_PAYOUT_FIELD_NAMES.pixAccountId] !== null}
            helperText={errorsMap[PIX_PAYOUT_FIELD_NAMES.pixAccountId]}
          />
        </FormControl>
      ) : null}
      <Button
        disabled={isSubmitDisabled}
        variant="contained"
        color="primary"
        onClick={handleSave}
        className={classes.button}
      >
        <Trans {...messages.saveAndContinue} />
      </Button>
    </>
  )
}

function buildFieldsVisibilityMap(formFieldsValues: Record<string, any>) {
  return {
    [PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity]: true,
    [PIX_PAYOUT_FIELD_NAMES.pixAccountType]: true,
    [PIX_PAYOUT_FIELD_NAMES.pixAccountId]:
      formFieldsValues.pixAccountType &&
      ![PixAccountTypes.cpf.value, PixAccountTypes.cnpj.value].includes(
        formFieldsValues.pixAccountType,
      ),
  }
}

function getCustomerIdentityErrorState(
  value: string,
  corporateClientDetected: boolean,
): JSX.Element | null {
  const digitsOnlyRegex = /^\d+$/
  const expectedDigits = corporateClientDetected ? 14 : 11

  const isValid =
    value.length === 0 || (digitsOnlyRegex.test(value) && value.length === expectedDigits)
  const customerIdentityLengthWarning = (
    <Trans {...messages.mustBeNDigits} values={{numDigits: expectedDigits}} />
  )
  return isValid ? null : customerIdentityLengthWarning
}

function getAccountIdErrorState(formFieldsValues: Record<string, any>): JSX.Element | null {
  if (formFieldsValues.pixAccountType !== PixAccountTypes.phone.value) return null
  const phoneNumber = formFieldsValues.pixAccountId
  if (!phoneNumber) return null

  const digitsOnlyRegex = /^\d+$/
  const expectedDigits = 11

  const isValid = digitsOnlyRegex.test(phoneNumber) && phoneNumber.length === expectedDigits
  const error = <Trans {...messages.pixPhoneNumberError} />
  return isValid ? null : error
}

function buildFieldsErrorStateMap(
  formFieldsValues: Record<string, any>,
  corporateClientDetected: boolean,
): {errorsMap: Record<PIX_PAYOUT_FIELD_NAMES, JSX.Element | null>; hasErrors: boolean} {
  const errorsMap = {
    [PIX_PAYOUT_FIELD_NAMES.pixCustomerIdentity]: getCustomerIdentityErrorState(
      formFieldsValues.pixCustomerIdentity,
      corporateClientDetected,
    ),
    [PIX_PAYOUT_FIELD_NAMES.pixAccountId]: getAccountIdErrorState(formFieldsValues),
    [PIX_PAYOUT_FIELD_NAMES.pixAccountType]: null,
  }
  const hasErrors = Object.values(errorsMap).some((error) => error !== null)
  return {errorsMap, hasErrors}
}
